// Content for the "esg-foundations-reporting" course slot, replaced with a
// faithful transcription of the real, already-delivered PowerPoint deck for
// this course's half-day format: "ESG Mastery: The Comprehensive Deep Dive"
// (half-day intensive · ~6 hours across six parts). Transcribed directly
// from the original 47-slide pptx text — every framework name, statistic
// and claim below is the real deck's own content, restructured into the web
// presentation system's slide shape (see SlideRenderer.js), not condensed
// or rewritten. manifest.id is kept as 'esg-foundations-reporting' so
// server.js continues to resolve this deck correctly.

import { applyOkiruTemplate } from '../okiruPresentationTemplate';

export const manifest = applyOkiruTemplate({
  id: 'esg-foundations-reporting',
  title: 'ESG Mastery: The Comprehensive Deep Dive',
  subtitle: 'Okiru Learn · Half-Day Intensive',
  modules: [
    { index: 1, name: 'Foundations' },
    { index: 2, name: 'Frameworks' },
    { index: 3, name: 'Jurisdictional Detail' },
    { index: 4, name: 'Company Application' },
    { index: 5, name: 'Technical Implementation' },
    { index: 6, name: 'Assessment & Roadmap' },
  ],
}, 'okiru-light');

export const slides = [
  {
    id: 1, layout: 'title',
    kicker: 'Half-Day Deep Dive · Comprehensive',
    title: 'ESG Mastery: The Comprehensive Deep Dive',
    lead: 'An extended, certified-expert-level session: every major framework in technical depth, a full worked materiality case study, GHG accounting mechanics, supply chain due diligence, and a knowledge check.',
    meta: ['Half-day intensive format', '~6 hours of content across six parts'],
  },
  {
    id: 2, layout: 'agenda',
    kicker: 'Agenda', title: 'Deep Dive Agenda — Six Parts',
    columns: [
      [
        { time: '1', label: 'Foundations' },
        { time: '2', label: 'Frameworks' },
        { time: '3', label: 'Jurisdictional Detail' },
      ],
      [
        { time: '4', label: 'Company Application' },
        { time: '5', label: 'Technical Implementation' },
        { time: '6', label: 'Assessment & Roadmap' },
      ],
    ],
  },
  {
    id: 3, layout: 'cards',
    kicker: 'Agenda', title: 'An Extended Sequence',
    lead: 'An extended sequence, adding regulatory depth, GHG mechanics, and a knowledge check to the standard workshop flow.',
    columns: 3,
    cards: [
      { title: 'Part 1: Foundations', description: 'ESG definitions, materiality, and the regulatory landscape.' },
      { title: 'Part 2: Frameworks', description: 'GRI, SASB, ISSB, TCFD, and CSRD compared in depth.' },
      { title: 'Part 3: Jurisdictional Detail', description: 'How adoption actually varies by region and what it means for you.' },
      { title: 'Part 4: Company Application', description: 'Materiality assessment, governance design, and target-setting.' },
      { title: 'Part 5: Technical Implementation', description: 'GHG accounting mechanics, supply chain due diligence, data systems.' },
      { title: 'Part 6: Assessment & Roadmap', description: 'Knowledge check, capstone roadmap exercise, and certification prep.' },
    ],
  },
  {
    id: 4, layout: 'cards',
    kicker: 'Agenda', title: 'Learning Objectives',
    lead: 'By the end of this session, you will be able to:',
    columns: 2,
    cards: [
      { title: 'Materiality types', description: 'Explain the difference between financial, impact, and double materiality, and apply each correctly.' },
      { title: 'Framework comparison', description: 'Compare GRI, SASB, ISSB (IFRS S1/S2), TCFD, and CSRD/ESRS on scope, audience, and disclosure basis.' },
      { title: 'Jurisdictional mapping', description: 'Map jurisdictional exposure and determine where mandatory disclosure obligations apply.' },
      { title: 'Materiality assessment', description: 'Conduct a basic materiality assessment for a company and prioritize ESG topics.' },
      { title: 'Governance design', description: 'Design an ESG governance structure with clear roles, escalation paths, and board oversight.' },
      { title: 'GHG calculation', description: 'Perform a basic GHG emissions calculation and explain organizational/operational boundaries.' },
      { title: 'Data & assurance', description: 'Identify data sources, gaps, and assurance requirements for credible disclosure.' },
      { title: 'Roadmap building', description: 'Build a phased ESG implementation roadmap appropriate to organizational maturity.' },
    ],
  },

  // ---------- Part 1: Foundations ----------
  {
    id: 5, layout: 'intro', module: 1,
    kicker: 'Part 1', title: 'Foundations: Defining ESG & Materiality',
    lead: 'Establishing a shared, precise vocabulary before moving into frameworks and company application.',
    templateAsset: 'action-communication', imageChrome: 'none',
  },
  {
    id: 6, layout: 'cards', module: 1,
    kicker: 'Part 1', title: 'ESG: A Working Definition',
    lead: 'ESG is a structured framework for identifying, measuring, and disclosing non-financial factors that affect long-term enterprise value and stakeholder outcomes.',
    columns: 3,
    cards: [
      { title: 'Environmental', description: 'Climate & emissions · Energy & resource use · Water & waste · Biodiversity' },
      { title: 'Social', description: 'Labor & human rights · DEI · Health & safety · Community impact' },
      { title: 'Governance', description: 'Board oversight · Ethics & anti-corruption · Risk management · Executive pay' },
    ],
  },
  {
    id: 7, layout: 'comparison', module: 1,
    kicker: 'Part 1', title: 'Materiality: The Filtering Mechanism',
    lead: 'Without materiality, ESG reporting becomes an unfocused list of every possible issue. Materiality answers: what matters, to whom, and why?',
    columns: [
      { heading: 'Financial Materiality', items: [
        'Question: Could this issue reasonably affect the company’s cash flows, access to finance, or cost of capital?',
        'Perspective: outside-in (how the world affects the company)',
        'Frameworks: ISSB (IFRS S1/S2), SASB, TCFD',
        'Primary audience: investors, lenders, creditors',
        'Note: this is the sole basis IFRS S1 uses.',
      ] },
      { heading: 'Impact Materiality', items: [
        'Question: Does the company’s activity have a significant actual or potential effect on people or the environment?',
        'Perspective: inside-out (how the company affects the world)',
        'Frameworks: GRI; combined with financial materiality under CSRD/ESRS',
        'Primary audience: broader stakeholders (communities, NGOs, workers)',
      ] },
    ],
  },
  {
    id: 8, layout: 'keyterm', module: 1,
    kicker: 'Part 1', term: 'Double Materiality',
    definition: 'CSRD/ESRS formally combines both lenses — an issue is material if either threshold is met.',
    body: 'Financial materiality and impact materiality are assessed together: an issue is reportable if it meets either threshold, not only when both apply.',
    templateAsset: 'thinking-learner',
    callouts: [
      { label: 'Example issue: GHG emissions', text: 'Financially material (transition risk, carbon pricing exposure) AND impact-material (contributes to climate change) — clearly reportable under CSRD.' },
      { label: 'Example issue: local biodiversity loss', text: 'May be impact-material without being clearly financially material today — still reportable under double materiality, but not under IFRS S1/S2 alone.' },
    ],
  },
  {
    id: 9, layout: 'cards', module: 1,
    kicker: 'Part 1', title: 'Identifying Stakeholders for Materiality Input',
    lead: 'A credible materiality assessment draws on structured input from a defined stakeholder set.',
    columns: 3,
    cards: [
      { title: 'Investors & lenders', description: 'Cost of capital, risk-adjusted returns, portfolio screening criteria.' },
      { title: 'Employees', description: 'Working conditions, safety, DEI, career development.' },
      { title: 'Customers & suppliers', description: 'Product responsibility, supply chain standards, contractual requirements.' },
      { title: 'Regulators & policymakers', description: 'Legal compliance, disclosure mandates, sector-specific rules.' },
      { title: 'Communities & NGOs', description: 'Local environmental and social impact, license to operate.' },
      { title: 'Board & leadership', description: 'Strategic risk appetite, enterprise risk integration.' },
    ],
  },
  {
    id: 10, layout: 'numbered', module: 1,
    kicker: 'Part 1', title: 'The Materiality Matrix',
    lead: 'A common output of the assessment process — plotting issues by significance to stakeholders vs. significance to the business. X: Significance to stakeholders · Y: Significance to business/investors.',
    templateAsset: 'problem-solving',
    items: [
      { title: 'Top-right quadrant', description: 'Priority disclosure topics — material to both lenses.' },
      { title: 'Bottom-left', description: 'Lower priority for now — monitor, don’t ignore.' },
      { title: 'Top-left / bottom-right', description: 'Divergence between lenses — worth board discussion.' },
    ],
  },
  {
    id: 11, layout: 'timeline', module: 1,
    kicker: 'Worked Case Study', title: 'Case Study: Materiality Assessment Walkthrough',
    lead: 'A generic mid-cap logistics company works through the five-step process from start to finish.',
    templateAsset: 'problem-solving',
    steps: [
      { era: 'Long-list', title: '32 candidate topics', description: 'Started with 32 candidate topics drawn from GRI Sector Standards for logistics and SASB’s road transportation standard.' },
      { era: 'Stakeholder input', title: 'Four groups surveyed', description: 'Surveyed investors, large shippers (customers), drivers, and two NGOs focused on road safety and emissions.' },
      { era: 'Scoring', title: '1–10 on both axes', description: 'Each topic scored 1-10 on both financial and impact significance; driver safety and fleet emissions scored highest on both axes.' },
      { era: 'Matrix outcome', title: '12 priority topics', description: '12 topics landed in the top-right quadrant: fleet emissions, driver safety, fuel cost exposure, data security, and others.' },
      { era: 'Validation', title: 'Executive review', description: 'Findings presented to the executive team; two topics were reprioritized after CFO input on insurance cost trends.' },
      { era: 'Outcome', title: 'ISSB-aligned disclosure', description: 'The 12 material topics became the basis for the company’s first ISSB-aligned disclosure and KPI set.' },
    ],
  },
  {
    id: 12, layout: 'exercise', module: 1,
    kicker: 'Workshop Exercise', meta: '15 min · small groups', title: 'Exercise: Rank These Issues',
    lead: 'In small groups, plot each issue on the materiality matrix for a generic mid-size manufacturing company.',
    steps: [
      { title: 'GHG emissions', detail: '' },
      { title: 'Employee turnover', detail: '' },
      { title: 'Water usage at facilities', detail: '' },
      { title: 'Board diversity', detail: '' },
      { title: 'Data privacy', detail: '' },
      { title: 'Supply chain labor standards', detail: '' },
      { title: 'Packaging waste', detail: '' },
      { title: 'Executive compensation structure', detail: '' },
    ],
    checkFor: 'each issue has a plotted position on the matrix that the group can defend.',
  },

  // ---------- Part 2: Frameworks ----------
  {
    id: 13, layout: 'intro', module: 2,
    kicker: 'Part 2', title: 'The Framework Landscape in Depth',
    lead: 'Understanding how GRI, SASB, ISSB, TCFD, and CSRD relate, overlap, and differ — and when each applies.',
    templateAsset: 'action-communication', imageChrome: 'none',
  },
  {
    id: 14, layout: 'comparison', module: 2,
    kicker: 'Part 2 · GRI', title: 'GRI: The Global Reporting Initiative',
    lead: 'The most widely used sustainability reporting framework worldwide, built on impact materiality.',
    columns: [
      { heading: 'Key facts', items: [
        'Founded 1997; the longest-standing global sustainability standard.',
        'Organized into Universal, Sector, and Topic Standards.',
        'Voluntary in most jurisdictions, though some countries reference it in policy.',
        'Designed for broad stakeholder accountability, not only investors.',
      ] },
      { heading: 'When to use GRI', items: [
        'Broad stakeholder communication (sustainability report, NGOs, communities)',
        'Companies with significant operational footprint or impact on local communities',
        'Organizations wanting a comprehensive, topic-based reporting structure',
        'Often used alongside ISSB/CSRD, not as a replacement',
      ] },
    ],
  },
  {
    id: 15, layout: 'cards', module: 2,
    kicker: 'Part 2 · SASB', title: 'SASB: Industry-Specific, Investor-Focused',
    lead: 'The Sustainability Accounting Standards Board built financially material, industry-specific metrics — now part of the ISSB.',
    columns: 2,
    cards: [
      { title: '77 industry standards', description: 'Each industry has its own set of financially material disclosure topics and metrics.' },
      { title: 'Financial materiality only', description: 'Built explicitly for investor decision-making, not broad stakeholder reporting.' },
      { title: 'Now part of ISSB', description: 'SASB Standards were consolidated into the IFRS Foundation and underpin IFRS S2’s industry-based guidance.' },
      { title: 'Practical implication', description: 'If you know your GICS industry classification, the relevant SASB metrics are largely pre-defined.' },
    ],
  },
  {
    id: 16, layout: 'comparison', module: 2,
    kicker: 'Part 2 · ISSB', title: 'ISSB: IFRS S1 & S2 — The Emerging Global Baseline',
    lead: 'Issued in June 2023, now the fastest-growing global disclosure standard, integrating TCFD and SASB.',
    columns: [
      { heading: 'IFRS S1', items: ['General requirements for disclosing all material sustainability-related risks and opportunities affecting enterprise value.'] },
      { heading: 'IFRS S2', items: ['Climate-specific disclosures: GHG emissions, physical & transition risks, scenario analysis. Fully integrates TCFD.'] },
    ],
    callout: { label: 'Shared structure', text: 'Both standards share four core content pillars from TCFD: Governance, Strategy, Risk Management, and Metrics & Targets.' },
    footnote: 'Status: adopted or being adopted in 28+ jurisdictions (50%+ of global GDP) as of April 2026.',
  },
  {
    id: 17, layout: 'cards', module: 2,
    kicker: 'Part 2 · TCFD', title: 'TCFD: Still the Conceptual Backbone',
    lead: 'The Task Force on Climate-related Financial Disclosures is technically retired as a standalone body, but its structure lives on inside IFRS S2.',
    columns: 2,
    cards: [
      { title: 'Governance', description: 'Board and management oversight of climate-related risks and opportunities.' },
      { title: 'Strategy', description: 'Actual and potential impacts of climate risk on business, strategy, and financial planning.' },
      { title: 'Risk Management', description: 'Processes for identifying, assessing, and managing climate-related risk.' },
      { title: 'Metrics & Targets', description: 'Metrics used to assess and manage climate risk, including emissions data.' },
    ],
    footer: 'Practical note: if a company has already reported under TCFD, the transition to IFRS S2 is comparatively straightforward.',
  },
  {
    id: 18, layout: 'cards', module: 2,
    kicker: 'Part 2 · CSRD', title: 'CSRD / ESRS: The EU’s Mandatory Standard',
    lead: 'The Corporate Sustainability Reporting Directive requires detailed, assured, double-materiality-based disclosure for in-scope companies.',
    columns: 3,
    cards: [
      { title: 'Scope', description: 'Applies to large EU undertakings and EU-listed companies; also reaches non-EU parents with sufficient EU activity.' },
      { title: 'Standards', description: 'Uses European Sustainability Reporting Standards (ESRS), developed by EFRAG.' },
      { title: 'Materiality basis', description: 'Built on double materiality — both financial and impact materiality must be assessed.' },
      { title: 'Feb 2026 simplification', description: 'On 24 February 2026, EU ministers approved reducing CSRD’s scope as part of a broader simplification effort.' },
      { title: 'EFRAG amendments', description: 'EFRAG submitted draft ESRS amendments (Dec 2025) to improve interoperability with ISSB standards.' },
      { title: 'Implication', description: 'CSRD scope and detail are evolving — track EFRAG and EU Commission updates closely if you are in scope.' },
    ],
  },
  {
    id: 19, layout: 'cards', module: 2,
    kicker: 'Part 2', title: 'Framework Comparison at a Glance',
    lead: 'A practitioner’s reference table for selecting the right framework(s).',
    columns: 5,
    cards: [
      { title: 'GRI', description: 'Global, voluntary. Impact materiality.', tag: 'Broad stakeholders' },
      { title: 'SASB', description: 'Industry-specific. Financial materiality.', tag: 'Investors' },
      { title: 'ISSB (IFRS S1/S2)', description: 'Global baseline. Financial materiality.', tag: 'Investors, lenders' },
      { title: 'TCFD', description: 'Climate-specific (folded into IFRS S2). Financial materiality.', tag: 'Investors' },
      { title: 'CSRD / ESRS', description: 'EU mandatory. Double materiality.', tag: 'Investors + stakeholders' },
    ],
  },
  {
    id: 20, layout: 'cards', module: 2,
    kicker: 'Part 2', title: 'Interoperability: You Rarely Choose Just One',
    lead: 'Most multinational companies report under a combination of frameworks, mapped to avoid duplicate data collection.',
    columns: 3,
    cards: [
      { title: 'EU-listed multinational', description: 'CSRD/ESRS (mandatory) + ISSB-aligned disclosure for non-EU investors, with GRI for broader stakeholder reporting.' },
      { title: 'US-listed company with EU operations', description: 'SEC disclosure requirements + ISSB voluntary alignment, monitoring CSRD applicability through EU subsidiaries.' },
      { title: 'Emerging market exporter', description: 'ISSB as the baseline (per local roadmap) + GRI to meet international buyer expectations.' },
    ],
  },

  // ---------- Part 3: Jurisdictional Detail ----------
  {
    id: 21, layout: 'intro', module: 3,
    kicker: 'Part 3', title: 'Jurisdictional Detail: Where the Rules Actually Apply',
    lead: 'Global frameworks matter in the abstract — but compliance obligations are set jurisdiction by jurisdiction. This section grounds the picture in specifics.',
    templateAsset: 'action-communication', imageChrome: 'none',
  },
  {
    id: 22, layout: 'cards', module: 3,
    kicker: 'Part 3', title: 'Global ISSB Adoption: A Snapshot',
    lead: 'Adoption is accelerating but highly uneven in timing and mandatory status.',
    columns: 4,
    cards: [
      { title: 'European Union', description: 'CSRD/ESRS is the binding regime; scope reduced via Feb 2026 simplification. ISSB interoperability improving via EFRAG amendments.' },
      { title: 'United Kingdom', description: 'UK-endorsed ISSB-aligned standards in progress; mandatory disclosure for large companies expected from 2026 reporting periods.' },
      { title: 'United States', description: 'SEC climate rules and state-level regimes (e.g. California SB 261) operate alongside voluntary ISSB alignment.' },
      { title: 'Asia-Pacific & Latin America', description: 'Singapore, Malaysia, Brazil, Chile, and others have published phased ISSB adoption roadmaps through 2027-2030.' },
    ],
    footer: 'As of April 2026: 28 jurisdictions have adopted ISSB standards; a further 12 are planning adoption.',
  },
  {
    id: 23, layout: 'numbered', module: 3,
    kicker: 'Part 3', title: 'Special Topic: Extraterritorial Reach of CSRD',
    lead: 'A frequent misunderstanding — CSRD does not stop at the EU’s borders.',
    templateAsset: 'problem-solving',
    items: [
      { title: 'Subsidiary or branch can trigger scope', description: 'A non-EU parent company can fall within CSRD scope if its EU subsidiary or branch meets size thresholds.' },
      { title: 'EU turnover threshold', description: 'Net turnover generated in the EU above the relevant threshold (currently under revision through the 2026 simplification process) can trigger group-level reporting obligations.' },
      { title: 'Subsidiary-level reporting', description: 'Even where the parent itself is not in scope, EU subsidiaries may need to report at the subsidiary level.' },
      { title: 'Reassess after change', description: 'Always reassess scope after M&A activity, market entry, or significant revenue growth in the EU.' },
      { title: 'Practitioner tip', description: 'Build CSRD scope reassessment into your annual legal entity review, not just your sustainability calendar.' },
    ],
  },
  {
    id: 24, layout: 'exercise', module: 3,
    kicker: 'Workshop Exercise', meta: '15 min', title: 'Exercise: Map Your Jurisdictional Exposure',
    lead: 'List every jurisdiction your organization operates, sells, borrows, or lists in.',
    steps: [
      { title: 'Jurisdiction', detail: 'Name each place you operate, sell, borrow, or list in.' },
      { title: 'Activity type', detail: 'Note what kind of activity ties you to that jurisdiction.' },
      { title: 'Current obligation', detail: 'Note any current ESG disclosure obligation there.' },
      { title: 'Upcoming change', detail: 'Note any upcoming change to that obligation.' },
    ],
    checkFor: 'a completed table covering every jurisdiction you operate, sell, borrow, or list in, each with a current obligation and an upcoming-change note.',
  },

  // ---------- Part 4: Company Application ----------
  {
    id: 25, layout: 'intro', module: 4,
    kicker: 'Part 4', title: 'Bringing It to Company Level',
    lead: 'Applying materiality, governance, and target-setting to a specific organization — the core skill this session builds.',
    templateAsset: 'team-collaboration', imageChrome: 'none',
  },
  {
    id: 26, layout: 'timeline', module: 4,
    kicker: 'Part 4', title: 'From Global Concept to Company Practice',
    lead: 'A repeatable five-step process for translating frameworks into an operating ESG program. We will work through each step using a generic illustrative company as a running example.',
    templateAsset: 'take-action',
    steps: [
      { era: 'Step 1', title: 'Scope & Applicability', description: 'Before assessing materiality, establish which regulations and frameworks actually apply.' },
      { era: 'Step 2', title: 'Materiality Assessment', description: 'A structured, repeatable process — not a one-time checkbox exercise.' },
      { era: 'Step 3', title: 'Governance Design', description: 'Credible disclosure requires a governance structure that an auditor or regulator can clearly trace.' },
      { era: 'Step 4', title: 'Target & KPI Setting', description: 'Targets should be specific, time-bound, and tied to a verifiable baseline.' },
      { era: 'Step 5', title: 'Disclosure Planning', description: 'Translate your materiality, governance, and KPI work into the actual disclosure document and process.' },
    ],
  },
  {
    id: 27, layout: 'numbered', module: 4,
    kicker: 'Part 4 · Step 1', title: 'Determining Scope & Applicability',
    lead: 'Before assessing materiality, establish which regulations and frameworks actually apply.',
    templateAsset: 'problem-solving',
    items: [
      { title: 'Where are we listed or headquartered?', description: 'Determines exposure to mandatory regimes (e.g. CSRD, SEC climate rules, national ISSB adoption).' },
      { title: 'What size and revenue thresholds do we meet?', description: 'Many regimes set headcount, turnover, or balance sheet thresholds for applicability.' },
      { title: 'Do major customers or financiers require disclosure?', description: 'Contractual requirements can trigger voluntary-but-essential reporting even without legal mandate.' },
      { title: 'Do we have subsidiaries or operations triggering other regimes?', description: 'A non-EU parent with sufficient EU revenue can still fall within CSRD scope.' },
    ],
  },
  {
    id: 28, layout: 'timeline', module: 4,
    kicker: 'Part 4 · Step 2', title: 'Conducting a Materiality Assessment',
    lead: 'A structured, repeatable process — not a one-time checkbox exercise.',
    templateAsset: 'problem-solving',
    steps: [
      { era: 'Step 1', title: 'Identify a long-list of topics', description: 'Use framework topic lists (GRI Universal/Sector Standards, SASB industry standards) as a starting point.' },
      { era: 'Step 2', title: 'Engage stakeholders', description: 'Surveys, interviews, or workshops with the groups identified earlier — internal and external.' },
      { era: 'Step 3', title: 'Score each topic', description: 'Rate financial significance and impact significance, typically on a 1-5 or 1-10 scale.' },
      { era: 'Step 4', title: 'Plot and validate', description: 'Build the materiality matrix; validate findings with leadership and subject-matter experts.' },
      { era: 'Step 5', title: 'Document and review periodically', description: 'Materiality shifts — reassess at least every 1-2 years or after major business changes.' },
    ],
  },
  {
    id: 29, layout: 'exercise', module: 4,
    kicker: 'Workshop Exercise', meta: '10 min · in pairs', title: 'Exercise: Scope a Fictional Company',
    lead: 'Work in pairs to determine applicable frameworks for the profile below.',
    steps: [
      { title: 'Headquartered outside the EU, mid-cap industrial manufacturer', detail: '' },
      { title: '1,800 employees globally; €420M annual revenue', detail: '' },
      { title: '30% of revenue from EU-based customers via a German subsidiary', detail: '' },
      { title: 'Listed on a domestic stock exchange that has announced ISSB alignment for 2027', detail: '' },
      { title: 'Largest customer (a global automaker) requires Scope 1-3 emissions data annually', detail: '' },
    ],
    checkFor: 'Is this company likely in CSRD scope? What about ISSB? What should it prioritize first?',
  },
  {
    id: 30, layout: 'numbered', module: 4,
    kicker: 'Part 4 · Step 3', title: 'Designing ESG Governance',
    lead: 'Credible disclosure requires a governance structure that an auditor or regulator can clearly trace.',
    templateAsset: 'icon-ownership',
    items: [
      { title: 'Board / Audit Committee', description: 'Approves ESG strategy and disclosures; oversees risk; reviews assurance results.' },
      { title: 'Executive Sponsor', description: 'Owns ESG strategy execution; reports to the board; resolves cross-functional blockers.' },
      { title: 'ESG Working Group', description: 'Cross-functional team coordinating data collection and topic ownership.' },
      { title: 'Data & Systems Owner', description: 'Maintains the underlying data infrastructure and internal controls.' },
      { title: 'Internal Audit', description: 'Tests controls ahead of external assurance; flags data quality issues early.' },
    ],
  },
  {
    id: 31, layout: 'cards', module: 4,
    kicker: 'Part 4', title: 'Common Governance Pitfalls',
    lead: 'Patterns practitioners repeatedly see in early-stage ESG programs.',
    columns: 2,
    cards: [
      { title: 'Single-owner risk', description: 'ESG sits with one person who lacks authority to compel data from business units.' },
      { title: 'No board reporting line', description: 'ESG strategy is set without board visibility, creating disclosure and oversight gaps.' },
      { title: 'No escalation path', description: 'Data quality issues surface only at year-end, with no time to remediate before disclosure.' },
      { title: 'Disconnected from enterprise risk', description: 'ESG risk register sits separately from the company’s main risk management process.' },
    ],
  },
  {
    id: 32, layout: 'numbered', module: 4,
    kicker: 'Part 4 · Step 4', title: 'Setting Credible Targets & KPIs',
    lead: 'Targets should be specific, time-bound, and tied to a verifiable baseline.',
    templateAsset: 'icon-consistency',
    items: [
      { title: 'Baseline first', description: 'Establish a verified baseline year before setting any reduction or improvement target.' },
      { title: 'Specific & time-bound', description: '"Reduce Scope 1+2 emissions 30% by 2030 vs. 2023 baseline" — not vague aspirations.' },
      { title: 'Tied to a recognized methodology', description: 'Where possible, align targets to frameworks like the Science Based Targets initiative.' },
      { title: 'Owned and tracked', description: 'Each target needs a named owner and a regular review cadence (quarterly is common).' },
    ],
  },
  {
    id: 33, layout: 'cards', module: 4,
    kicker: 'Part 4', title: 'Illustrative KPI Library by Pillar',
    lead: 'A starting reference — actual KPIs should be filtered through your materiality assessment.',
    columns: 3,
    cards: [
      { title: 'Environmental', description: 'Scope 1, 2, 3 GHG emissions (tCO2e) · Energy consumption & renewable % · Water withdrawal & discharge · Waste generated & diverted from landfill' },
      { title: 'Social', description: 'Employee turnover rate · Lost-time injury frequency rate · Gender pay gap · % of supply chain audited for labor standards' },
      { title: 'Governance', description: '% independent board members · Board ESG expertise / oversight frequency · Anti-corruption training completion rate · Whistleblower cases resolved' },
    ],
  },
  {
    id: 34, layout: 'numbered', module: 4,
    kicker: 'Part 4 · Step 5', title: 'Planning the Disclosure Itself',
    lead: 'Translate your materiality, governance, and KPI work into the actual disclosure document and process.',
    templateAsset: 'action-communication',
    items: [
      { title: 'Map material topics to framework requirements', description: 'Map material topics to specific framework disclosure requirements (e.g. ESRS data points, IFRS S2 metrics).' },
      { title: 'Decide the disclosure vehicle', description: 'Standalone sustainability report, integrated annual report, or regulatory filing.' },
      { title: 'Build an internal sign-off process', description: 'Mirroring financial reporting controls.' },
      { title: 'Plan assurance scope and timeline', description: 'Limited assurance is the common starting point.' },
      { title: 'Establish a disclosure calendar', description: 'Aligned to financial reporting deadlines.' },
    ],
  },

  // ---------- Part 5: Technical Implementation ----------
  {
    id: 35, layout: 'intro', module: 5,
    kicker: 'Part 5', title: 'Implementation: Data, Assurance & Roadmap',
    lead: 'The operational backbone that makes ongoing disclosure sustainable rather than a yearly fire drill.',
    templateAsset: 'workshop-facilitator', imageChrome: 'none',
  },
  {
    id: 36, layout: 'numbered', module: 5,
    kicker: 'Part 5', title: 'Building ESG Data Infrastructure',
    lead: 'Most ESG programs stall not on strategy, but on the difficulty of getting reliable, auditable data.',
    templateAsset: 'problem-solving',
    items: [
      { title: 'Centralize data sources', description: 'Avoid disparate spreadsheets — use a system of record (dedicated ESG software or integrated finance modules).' },
      { title: 'Map data owners', description: 'Each metric needs a single accountable owner, ideally embedded in existing operational reporting.' },
      { title: 'Build in validation checks', description: 'Range checks, year-over-year variance flags, and unit consistency checks reduce error rates.' },
      { title: 'Document methodology', description: 'Calculation methodologies must be documented clearly enough for an external assurer to follow.' },
    ],
  },
  {
    id: 37, layout: 'comparison', module: 5,
    kicker: 'Part 5', title: 'Special Topic: The Scope 3 Data Challenge',
    lead: 'Scope 3 emissions are typically 70%+ of a company’s footprint, yet the hardest data to obtain reliably.',
    columns: [
      { heading: 'Common approaches', items: [
        'Spend-based estimation (lowest accuracy, fastest to start)',
        'Industry-average emission factors per activity',
        'Supplier-specific data (highest accuracy, hardest to collect)',
        'Hybrid approach, improving accuracy of top suppliers over time',
      ] },
      { heading: 'Practical guidance', items: [
        'Start with spend-based estimates to establish a baseline quickly.',
        'Prioritize supplier engagement for your top 20% of spend (often 80% of impact).',
        'ISSB amendments (Dec 2025) aim to ease GHG disclosure complexity — track applicability.',
        'Be transparent about methodology and estimation level in your disclosure.',
      ] },
    ],
  },
  {
    id: 38, layout: 'comparison', module: 5,
    kicker: 'Part 5', title: 'Assurance Readiness',
    lead: 'Many mandatory regimes now require independent assurance over ESG disclosures, starting with limited assurance.',
    columns: [
      { heading: 'Limited Assurance', items: ['Auditor performs inquiries and analytical procedures; concludes "nothing has come to our attention" — a lower bar than financial audit.'] },
      { heading: 'Reasonable Assurance', items: ['Equivalent rigor to a financial statement audit; auditor expresses a positive opinion on the disclosures.'] },
    ],
    callout: { label: 'Readiness checklist', text: 'Documented methodologies for each material metric · Evidence trails (source documents, calculations) retained · Internal review/sign-off completed before external assurance begins' },
  },
  {
    id: 39, layout: 'numbered', module: 5,
    kicker: 'Part 5 · Risk', title: 'Avoiding Greenwashing Risk',
    lead: 'Regulatory and reputational scrutiny of unsubstantiated ESG claims has intensified sharply.',
    templateAsset: 'icon-consistency',
    items: [
      { title: 'Evidence every claim', description: 'Only make claims you can evidence with verifiable, current data.' },
      { title: 'Avoid vague terms', description: 'Avoid vague terms ("eco-friendly," "green") without specific, substantiated meaning.' },
      { title: 'Disclose methodology', description: 'Disclose methodology and assumptions, not just the headline number.' },
      { title: 'Keep claims current', description: 'Update claims when underlying data or targets change — don’t let claims go stale.' },
    ],
  },
  {
    id: 40, layout: 'exercise', module: 5,
    kicker: 'Workshop Exercise', meta: '20 min · in groups', title: 'Capstone Exercise: Build a 12-Month Roadmap',
    lead: 'Using everything covered today, draft a roadmap for your own organization (or the exercise company).',
    steps: [
      { title: 'Scoping conclusion', detail: 'What is your scoping conclusion — which framework(s) apply, and by when?' },
      { title: 'Top material topics', detail: 'What are your top 5 material topics, and how confident are you in that assessment?' },
      { title: 'Governance structure', detail: 'What governance structure will you propose, and who is the executive sponsor?' },
      { title: 'Quick wins', detail: 'What are 2-3 realistic quick wins within the next 2 quarters?' },
      { title: 'Biggest data gap', detail: 'What is your single biggest data gap, and what’s the first step to closing it?' },
    ],
    checkFor: 'a roadmap that names its framework scope, its top material topics, its governance owner, its quick wins, and its biggest data gap.',
  },
  {
    id: 41, layout: 'timeline', module: 5,
    kicker: 'Part 5', title: 'A Generic 12-Month Implementation Roadmap',
    lead: 'Use this as a starting template — actual timing depends on regulatory deadlines and starting maturity.',
    templateAsset: 'take-action',
    steps: [
      { era: 'Q1', title: 'Scope & governance charter', description: 'Scope & gap assessment · Stakeholder engagement begins · Governance charter drafted' },
      { era: 'Q2', title: 'Materiality & data infrastructure', description: 'Materiality assessment completed · Data infrastructure selection · KPI library finalized' },
      { era: 'Q3', title: 'Data collection & target-setting', description: 'Data collection & validation · Target-setting workshops · Draft disclosure outline' },
      { era: 'Q4', title: 'Sign-off & first disclosure', description: 'Internal review & sign-off · Assurance scoping · First disclosure published' },
    ],
  },
  {
    id: 42, layout: 'cards', module: 5,
    kicker: 'Part 5', title: 'GHG Accounting Mechanics',
    lead: 'The core calculation logic behind every emissions disclosure — understanding this prevents common audit findings.',
    columns: 2,
    cards: [
      { title: 'Activity data × emission factor', description: 'The basic formula: a quantity of activity (fuel burned, electricity used) multiplied by a factor converting it to CO2-equivalent.' },
      { title: 'Organizational boundary', description: 'Choose equity share, financial control, or operational control — this determines which entities’ emissions you consolidate.' },
      { title: 'Operational boundary', description: 'Defines which Scope 1, 2, and 3 categories are included — should align with your organizational boundary choice.' },
      { title: 'GHG Protocol as the basis', description: 'Almost all frameworks (ISSB, CSRD, GRI) reference the GHG Protocol Corporate Standard as the underlying methodology.' },
    ],
  },
  {
    id: 43, layout: 'lead', module: 5,
    kicker: 'Part 5', title: 'Worked Example: Scope 1 Calculation',
    lead: 'A simplified illustrative calculation for a single emissions source. Scenario: a facility burns 50,000 liters of diesel for backup generators in one year.',
    body: 'Step 1 — Activity data: 50,000 liters of diesel consumed. Step 2 — Emission factor: ≈2.68 kg CO2e per liter of diesel (illustrative factor; use current published factors). Step 3 — Calculation: 50,000 × 2.68 kg = 134,000 kg CO2e = 134 tCO2e. Step 4 — Classification: Reported as Scope 1 (direct emissions from owned equipment).',
    templateAsset: 'problem-solving', imageChrome: 'none',
    callout: { label: 'Note', text: 'Always use the most current published emission factors (e.g. from DEFRA, EPA, or IPCC) and disclose the source.' },
  },
  {
    id: 44, layout: 'numbered', module: 5,
    kicker: 'Part 5', title: 'Supply Chain Due Diligence',
    lead: 'Human rights and environmental due diligence obligations increasingly extend ESG accountability into the supply chain.',
    templateAsset: 'conversation-coaching',
    items: [
      { title: 'Map the supply chain', description: 'Map your supply chain by tier, prioritizing by spend and risk (geography, sector, labor practices).' },
      { title: 'Issue supplier codes of conduct', description: 'Covering labor, environmental, and anti-corruption standards.' },
      { title: 'Build an audit and verification cadence', description: 'For high-risk suppliers, with remediation plans for findings.' },
      { title: 'Maintain a grievance mechanism', description: 'Accessible to workers across the supply chain, not just direct employees.' },
      { title: 'Document due diligence processes', description: 'Increasingly a disclosure requirement in its own right (e.g. under CSRD/ESRS).' },
    ],
  },

  // ---------- Part 6: Assessment & Roadmap ----------
  {
    id: 45, layout: 'intro', module: 6,
    kicker: 'Part 6', title: 'Assessment & Final Roadmap',
    lead: 'Consolidating the day’s material with a structured knowledge check before the capstone planning exercise.',
    templateAsset: 'team-collaboration', imageChrome: 'none',
  },
  {
    id: 46, layout: 'cards', module: 6,
    kicker: 'Part 6', title: 'Knowledge Check',
    lead: 'Test your understanding before the capstone exercise. Discuss answers as a group.',
    columns: 5,
    cards: [
      { title: 'Which materiality basis does IFRS S1 use exclusively?', description: 'Financial materiality' },
      { title: 'What does "double materiality" combine?', description: 'Financial materiality and impact materiality' },
      { title: 'Which body’s recommendations are now fully integrated into IFRS S2?', description: 'TCFD' },
      { title: 'Approximately what share of global GDP is represented by ISSB-adopting jurisdictions (as of April 2026)?', description: 'More than 50%' },
      { title: 'What is typically the largest share of a company’s GHG footprint?', description: 'Scope 3 emissions' },
    ],
  },

  // ---------- Reference ----------
  {
    id: 47, layout: 'cards',
    kicker: 'Reference', title: 'Quick-Reference Glossary (1 of 2)',
    lead: 'Key terms used throughout this session.',
    columns: 5,
    cards: [
      { title: 'Materiality', description: 'The threshold for whether an ESG issue is significant enough to report or act on.' },
      { title: 'Double materiality', description: 'Considering both financial materiality and impact materiality together (CSRD).' },
      { title: 'Assurance', description: 'Independent verification of reported ESG data (limited or reasonable).' },
      { title: 'Scope 1 / 2 / 3', description: 'GHG emissions categories by source and control across the value chain.' },
      { title: 'Greenwashing', description: 'Making misleading or unsubstantiated claims about environmental performance.' },
    ],
  },
  {
    id: 48, layout: 'cards',
    kicker: 'Reference', title: 'Quick-Reference Glossary (2 of 2)',
    columns: 5,
    cards: [
      { title: 'Baseline year', description: 'The reference year against which future ESG performance is measured.' },
      { title: 'ESRS', description: 'European Sustainability Reporting Standards — the technical standards under CSRD.' },
      { title: 'GICS', description: 'Global Industry Classification Standard — used to map SASB’s industry-specific metrics.' },
      { title: 'Organizational boundary', description: 'The consolidation approach (equity share, financial or operational control) for GHG accounting.' },
      { title: 'GHG Protocol', description: 'The widely-used methodology underpinning most corporate GHG accounting and reporting standards.' },
    ],
  },

  // ---------- Close ----------
  {
    id: 49, layout: 'outcome',
    kicker: 'Key Takeaways', title: 'What You’ve Learned',
    checks: [
      'Materiality — financial, impact, or double — is the filter that makes ESG reporting manageable.',
      'Frameworks are complementary, not competing; most companies use more than one, and jurisdictional detail determines what’s mandatory.',
      'Governance, GHG accounting rigor, and supply chain due diligence determine whether disclosure is credible and assurance-ready.',
      'A phased roadmap, anchored in your specific materiality findings, turns strategy into execution.',
    ],
    footnote: 'You’ve completed the comprehensive ESG deep dive.',
  },
  {
    id: 50, layout: 'statement',
    text: 'Thank You',
    footnote: 'Use the roadmap and worksheets from today as the starting point for your organization’s ESG program. Revisit your materiality assessment annually.',
  },
];
